import { useI18n } from "../../i18n";
import { cn } from "../cn";
import { useOnboarding } from "./useOnboarding";

const go = (hash: string) => {
  window.location.hash = hash;
};

/**
 * OnboardingEntryBanner — Macro #27 entry point for the post-login / dashboard
 * surface. Eligible (signed-in, not completed, not dismissed) users see
 * START / NOT NOW; NOT NOW dismisses (no nag loop). Dismissed-not-completed
 * users get a compact RESUME chip; completed users get a RESTART chip.
 */
export function OnboardingEntryBanner({ className }: { className?: string }) {
  const { t } = useI18n();
  const onb = useOnboarding();

  if (onb.status !== "ready" || !onb.state) return null;
  const { completed, dismissed } = onb.state;

  if (completed) {
    return (
      <div
        data-testid="onboarding-entry-completed"
        className={cn("flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-soft bg-panel px-4 py-3", className)}
      >
        <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.06em] text-neon-teal">
          <span aria-hidden className="h-1.5 w-1.5 rounded-pill bg-neon-teal" />
          {t("onboarding.entry.completed")}
        </span>
        <button
          data-testid="onboarding-entry-restart"
          onClick={() => {
            void onb.restart();
            go("#onboarding");
          }}
          className="inline-flex items-center h-8 px-3 no-underline font-mono text-[10px] tracking-[0.16em] uppercase text-text-secondary neon-glass pill-ghost hover:text-text-primary"
        >
          {t("onboarding.entry.restart")}
        </button>
      </div>
    );
  }

  if (dismissed) {
    return (
      <div
        data-testid="onboarding-entry-resume"
        className={cn("flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border-soft bg-panel px-4 py-3", className)}
      >
        <span className="font-mono text-[11px] tracking-[0.06em] text-text-secondary">{t("onboarding.entry.title")}</span>
        <button
          data-testid="onboarding-entry-resume-btn"
          onClick={() => {
            void onb.patch({ dismissed: false });
            go("#onboarding");
          }}
          className="facet-btn h-8 px-4 text-[10.5px] font-semibold tracking-[0.16em]"
        >
          {t("onboarding.entry.resume")}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="onboarding-entry-banner"
      className={cn("rounded-lg border border-border-accent bg-panel neon-glass p-5 shadow-glow-teal-sm", className)}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neon-teal">{t("onboarding.entry.eyebrow")}</div>
          <h2 className="mt-1 font-display text-lg font-semibold tracking-[-0.01em] text-text-primary">{t("onboarding.entry.title")}</h2>
          <p className="mt-1 font-display text-[12.5px] leading-[1.55] text-text-secondary max-w-[560px]">{t("onboarding.entry.body")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            data-testid="onboarding-entry-start"
            onClick={() => go("#onboarding")}
            className="facet-btn h-9 px-5 text-[11px] font-semibold tracking-[0.18em]"
          >
            {t("onboarding.entry.start")}
          </button>
          <button
            data-testid="onboarding-entry-not-now"
            onClick={() => void onb.patch({ dismissed: true })}
            disabled={onb.mutating}
            className="inline-flex items-center h-9 px-4 font-mono text-[10.5px] tracking-[0.16em] uppercase text-text-muted hover:text-text-secondary disabled:opacity-50"
          >
            {t("onboarding.entry.notNow")}
          </button>
        </div>
      </div>
    </div>
  );
}
